import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import styled from 'styled-components';
import ErrorHandler from '../../components/ErrorHandler';
import { scrollToTop } from '../../utils/scrollHelper';

const Container = styled.div`
  max-width: 500px;
  margin: 3rem auto;
  padding: 2rem;
  background-color: var(--white);
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: var(--primary);
  margin-bottom: 1.5rem;
  text-align: center;
  font-size: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--gray);
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Button = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: var(--primary-dark);
  }
  
  &:disabled {
    background-color: var(--gray);
    cursor: not-allowed;
  }
`;

const Message = styled.p`
  margin-bottom: 1.5rem;
  text-align: center;
`;

const BackLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  
  a {
    color: var(--primary);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SuccessMessage = styled.div`
  background-color: rgba(126, 203, 185, 0.1);
  border-left: 4px solid var(--secondary);
  color: var(--secondary-dark);
  padding: 16px;
  margin: 16px 0;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const SuccessTitle = styled.h4`
  margin: 0 0 8px 0;
  font-weight: 600;
  color: var(--secondary-dark);
`;

const SuccessText = styled.p`
  margin: 0;
  line-height: 1.5;
`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  
  const { forgotPassword, confirmPassword: resetPassword } = useAuth();
  const navigate = useNavigate();
  
  const clearError = () => {
    setErrorMessage('');
  };
  
  const handleRequestCode = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setIsLoading(true);
    
    try {
      await forgotPassword(email);
      setCodeSent(true);
      setSuccessMessage(`Verification code sent to ${email}`);
    } catch (error) {
      console.error('Forgot password error:', error);
      setErrorMessage(error.message || 'Failed to send reset code.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }
    
    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters long.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await resetPassword(email, code, newPassword);
      scrollToTop();
      navigate('/login', { 
        state: { message: 'Password reset successfully! You can now log in with your new password.' } 
      });
    } catch (error) {
      console.error('Reset password error:', error);
      
      // Format specific error messages for better user experience
      if (error.message && error.message.includes('Invalid verification code')) {
        setErrorMessage('The verification code you entered is invalid or has expired. Please try again.');
      } else if (error.message && error.message.includes('Password does not conform to policy')) {
        setErrorMessage('Your password must be at least 8 characters long and include uppercase, lowercase, numbers, and special characters.');
      } else {
        setErrorMessage(error.message || 'Failed to reset password.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Container>
      <Title>Reset Password</Title>
      
      {errorMessage && (
        <ErrorHandler 
          error={errorMessage} 
          clearError={clearError} 
        />
      )}
      
      {successMessage && (
        <SuccessMessage>
          <SuccessTitle>Success</SuccessTitle>
          <SuccessText>{successMessage}</SuccessText>
        </SuccessMessage>
      )}
      
      {!codeSent ? (
        <>
          <Message>
            Enter your email address and we'll send you a code to reset your password.
          </Message>
          
          <Form onSubmit={handleRequestCode}>
            <FormGroup>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </FormGroup>
            
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Reset Code'}
            </Button>
          </Form>
        </>
      ) : (
        <>
          <Message>
            We've sent a verification code to <strong>{email}</strong>.
            Enter the code and your new password below.
          </Message>
          
          <Form onSubmit={handleResetPassword}>
            <FormGroup>
              <Label htmlFor="code">Verification Code</Label>
              <Input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                placeholder="Enter verification code"
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Enter new password"
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm new password"
              />
            </FormGroup>
            
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </Form>
        </>
      )}
      
      <BackLink>
        <a href="/login" onClick={(e) => {
          e.preventDefault();
          scrollToTop();
          navigate('/login');
        }}>Back to Login</a>
      </BackLink>
    </Container>
  );
};

export default ForgotPasswordPage;
